import { useState, useEffect } from 'react';
import { ChevronsUpDown, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { graphqlApi } from '@/lib/graphql';
import { API_BASE_URL } from '@/lib/api';
import { useAuthStore } from '@/stores/auth-store';
import { useToastStore } from '@/stores/toast-store';
import { useStoreStore } from '@/stores/store-store';
// import { useLanguage } from '@/contexts/LanguageContext';

interface GraphQLOrder {
  id: string;
  reference: string;
  order_date: string;
  status: string;
  payment_status: string;
  customer: {
    email: string;
    phone: string;
  };
  market_place_type: {
    name: string;
  };
  city: {
    name: string;
  };
  order_products_count: number;
  order_products: Array<{
    id: string;
    code: string;
    first_identifier: string;
    second_identifier: string;
    marketplace_order_product: {
      name: string;
    };
  }>;
}

interface SplitTableOrder {
  id: string;
  identifier: string;
  orderDate: string;
  zone: string;
}

function CustomCheckbox({ checked, onChange }: { checked: boolean; onChange: (checked: boolean) => void }) {
  return (
    <div className="relative">
      <input 
        type="checkbox" 
        checked={checked}
        onChange={(e) => onChange(e.target.checked)}
        className="peer w-4 h-4 rounded border-2 border-gray-300 text-blue-600 focus:ring-blue-500 focus:ring-2 bg-white appearance-none checked:bg-blue-600 checked:border-blue-600" 
      />
      <svg 
        className="absolute inset-0 w-4 h-4 pointer-events-none opacity-0 peer-checked:opacity-100 text-white" 
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
      </svg>
    </div>
  );
}

export default function SplitOrdersTable({ 
  searchQuery,
  filters,
  storeId,
  status
}: { 
  searchQuery?: string;
  filters?: any;
  storeId?: number;
  status?: string;
}) {
  // const { t } = useLanguage();
  const { token } = useAuthStore();
  const { currentStore } = useStoreStore();
  const storeName = currentStore?.name?.toLowerCase()?.replace(/\s+/g, '') ?? '';
  
  // State for split table
  const [leftTableOrders, setLeftTableOrders] = useState<SplitTableOrder[]>([]);
  const [rightTableOrders, setRightTableOrders] = useState<SplitTableOrder[]>([]);
  const [selectedLeftItems, setSelectedLeftItems] = useState<Set<string>>(new Set());
  const [selectedRightItems, setSelectedRightItems] = useState<Set<string>>(new Set());
  const [isDragOver, setIsDragOver] = useState(false);
  
  // State for API
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const { addToast } = useToastStore();

  // Fetch data from GraphQL
  const fetchOrders = async () => {
    if (!token) return;

    setIsLoading(true);
    setError(null);

        try {
          // To Center source must mirror destination_warehouse/in_warehouse records from arrived-shipments.
          if (status === 'received-at-destination-warehouse') {
            if (!storeName) {
              throw new Error('Store name is required');
            }

            const params = new URLSearchParams({
              store_name: storeName,
              page: String(page),
              limit: String(itemsPerPage),
            });
            if (searchTerm && searchTerm.trim() !== '') params.set('search', searchTerm.trim());

            const res = await fetch(`${API_BASE_URL}/warehouse/arrived-shipments?${params.toString()}`, {
              headers: {
                accept: 'application/json',
                Authorization: `Bearer ${token}`,
              },
            });
            const json = await res.json();
            if (!res.ok) {
              throw new Error(json?.message || 'Failed to fetch arrived shipments');
            }

            const rows: any[] = json?.data?.records?.data || [];
            const transformedOrders: SplitTableOrder[] = rows.map((item: any) => ({
              id: String(item.order_product_id ?? item.id),
              identifier: item.item_order_id || String(item.order_product_id ?? item.id),
              orderDate: item.order_confirmed_date
                ? new Date(item.order_confirmed_date).toLocaleDateString('en-US', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric',
                  })
                : '-',
              zone: item.shipment_reference || 'N/A',
            }));

            setLeftTableOrders(transformedOrders);
            setTotalItems(json?.data?.records?.total || transformedOrders.length);
            return;
          }

       // Use marketplace filter directly (uk_mkt, us_mkt format)
       let marketPlaceType = filters?.marketPlaceType || '';
       if (filters?.marketplaceFilter && filters.marketplaceFilter.length > 0) {
         // Use the marketplace filter values directly (uk_mkt, us_mkt format)
         marketPlaceType = filters.marketplaceFilter.join(',');
       }

               const variables = {
          status: status || "procured-but-not-delivered-to-facility",
          search: searchTerm || "",
          marketPlaceType: marketPlaceType,
          city: filters?.city || "",
          customerEmail: filters?.customerEmail || "",
          orderDateFrom: filters?.orderDateFrom || "",
          orderDateTo: filters?.orderDateTo || "",
          page: page,
          first: itemsPerPage,
          sort: "created_at", // Default sort by creation date
          order: "desc" // Default descending order
        };

      // Use the same API call pattern as the original OrdersTable
              const response = await graphqlApi.getOrderListing(token, storeId || 1, variables); // Use provided storeId or default to 1
      
      if (response.errors) {
        throw new Error(response.errors[0]?.message || 'GraphQL query failed');
      }

      if (response.data?.orderListing) {
        const orders = response.data.orderListing.data;
        const paginatorInfo = response.data.orderListing.paginatorInfo;
        
                 // Transform GraphQL data to split table format
         const transformedOrders: SplitTableOrder[] = orders.map((order: GraphQLOrder) => {
           // Get identifier from order products
           let identifier = order.reference; // fallback
           if (order.order_products && order.order_products.length > 0) {
             const firstProduct = order.order_products[0];
             // Use first_identifier-second_identifier format if available, otherwise use code
             if (firstProduct.first_identifier && firstProduct.second_identifier) {
               identifier = `${firstProduct.first_identifier}-${firstProduct.second_identifier}`;
             } else {
               identifier = firstProduct.code || order.reference;
             }
           }

           return {
             id: order.id,
             identifier: identifier,
             orderDate: new Date(order.order_date).toLocaleDateString('en-US', { 
               day: '2-digit', 
               month: 'short', 
               year: 'numeric' 
             }),
             zone: order.market_place_type?.name || 'Unknown'
           };
         });

        // Ship mode: expand orders to items
        if (status === 'delivered-to-facility') {
          const itemRows: SplitTableOrder[] = [];
          let itemCount = 0;
          for (const order of orders) {
            const prods = order.order_products || [];
            itemCount += prods.length;
            for (const p of prods) {
              let ident = p.code || order.reference;
              if (p.first_identifier && p.second_identifier) {
                ident = `${p.first_identifier}-${p.second_identifier}`;
              }
              itemRows.push({
                id: p.id,
                identifier: ident,
                orderDate: new Date(order.order_date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }),
                zone: order.market_place_type?.name || 'Unknown'
              });
            }
          }
          setLeftTableOrders(itemRows);
          setTotalItems(itemCount);
        } else {
          setLeftTableOrders(transformedOrders);
          setTotalItems(paginatorInfo.total);
        }
      }
    } catch (err) {
      console.error('Error fetching orders:', err);
      setError('Failed to fetch orders');
    } finally {
      setIsLoading(false);
    }
  };

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setPage(1); // Reset to first page when searching
      fetchOrders();
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Reset search term when parent search query changes
  useEffect(() => {
    if (searchQuery !== searchTerm) {
      setSearchTerm(searchQuery || '');
    }
  }, [searchQuery]);

  // Fetch data when component mounts or dependencies change
  useEffect(() => {
    fetchOrders();
  }, [page, itemsPerPage, searchQuery, filters, token, storeId, status, storeName]);

  // Notify parent when right table orders change
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('right-table-orders-update', { 
      detail: { orders: rightTableOrders } 
    }));
  }, [rightTableOrders]);

  // Listen for To Center submit: generate batch → update batch → assign center
  useEffect(() => {
    const handler = async () => {
      try {
        if (!token || !storeId) return;
        const ids = rightTableOrders.map(o => Number(o.id)).filter(n => Number.isFinite(n));
        if (ids.length === 0) {
          addToast({ type: 'warning', title: 'No Items', message: 'Drag items to the right before submitting.', duration: 3000 });
          return;
        }
        const city = filters?.city || '';
        const distributionCenterId = filters?.distributionCenterId || 0;
        if (!city) {
          addToast({ type: 'warning', title: 'Select City', message: 'Please select a City.', duration: 3000 });
          return;
        }

        // Generate batch id
        const genRes = await (await import('@/lib/api')).distributionApi.generateBatchId(token, storeId);
        if (!genRes.success || !genRes.data?.batchId) throw new Error(genRes.message || 'Failed to generate batch');
        const batchId = String(genRes.data.batchId);

        // Update batch id for items
        const updRes = await (await import('@/lib/api')).distributionApi.updateBatchId(token, storeId, ids, batchId);
        if (!updRes.success) throw new Error(updRes.message || 'Failed to update batch');

        // Complete distribution action (matches legacy business logic)
        // This endpoint handles both assignment and notifications, even if no distribution center is selected
        const actionRes = await (await import('@/lib/api')).distributionApi.distributionAction(token, storeId, ids, Number(distributionCenterId || 0));
        if (!actionRes.success) throw new Error(actionRes.message || 'Failed to complete distribution action');

        // Clear right table on success
        setRightTableOrders([]);
        addToast({ type: 'success', title: 'Sent to Center', message: `Batch ${batchId} created and processed successfully.`, duration: 3500 });
      } catch (e) {
        console.error('To Center submit failed:', e);
      }
    };
    window.addEventListener('to-center-submit', handler);
    return () => window.removeEventListener('to-center-submit', handler);
  }, [rightTableOrders, filters, token, storeId]);

  // Handle checkbox selection for left table
  const handleLeftTableSelection = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedLeftItems);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedLeftItems(newSelected);
  };

  // Handle checkbox selection for right table
  const handleRightTableSelection = (id: string, checked: boolean) => {
    const newSelected = new Set(selectedRightItems);
    if (checked) {
      newSelected.add(id);
    } else {
      newSelected.delete(id);
    }
    setSelectedRightItems(newSelected);
  };

  // Handle select all for left table
  const handleLeftTableSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = leftTableOrders.map(order => order.id);
      setSelectedLeftItems(new Set(allIds));
    } else {
      setSelectedLeftItems(new Set());
    }
  };

  // Handle select all for right table
  const handleRightTableSelectAll = (checked: boolean) => {
    if (checked) {
      const allIds = rightTableOrders.map(order => order.id);
      setSelectedRightItems(new Set(allIds));
    } else {
      setSelectedRightItems(new Set());
    }
  };

  // Handle Add button click
  const handleAddItems = () => {
    const itemsToAdd = leftTableOrders.filter(order => selectedLeftItems.has(order.id));
    setRightTableOrders(prev => [...prev, ...itemsToAdd]);
    setLeftTableOrders(prev => prev.filter(order => !selectedLeftItems.has(order.id)));
    setSelectedLeftItems(new Set());
  };

  // Handle Remove button click
  const handleRemoveItems = () => {
    const itemsToRemove = rightTableOrders.filter(order => selectedRightItems.has(order.id));
    setLeftTableOrders(prev => [...prev, ...itemsToRemove]);
    setRightTableOrders(prev => prev.filter(order => !selectedRightItems.has(order.id)));
    setSelectedRightItems(new Set());
  };

  // Handle drag and drop
  const handleDragStart = (e: React.DragEvent, item: SplitTableOrder, fromLeftTable: boolean) => {
    e.dataTransfer.setData('text/plain', JSON.stringify({ ...item, fromLeftTable }));
  };

  const handleLeftTableDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleRightTableDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleLeftTableDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    try {
      const draggedData = JSON.parse(e.dataTransfer.getData('text/plain')) as SplitTableOrder & { fromLeftTable: boolean };
      
      if (!draggedData.fromLeftTable) {
        // Moving from right table to left table
        setRightTableOrders(prev => prev.filter(order => order.id !== draggedData.id));
        setLeftTableOrders(prev => [...prev, draggedData]);
      }
    } catch (error) {
      console.error('Error parsing dragged item:', error);
    }
  };

  const handleRightTableDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    try {
      const draggedData = JSON.parse(e.dataTransfer.getData('text/plain')) as SplitTableOrder & { fromLeftTable: boolean };
      
      if (draggedData.fromLeftTable) {
        // Moving from left table to right table
        setLeftTableOrders(prev => prev.filter(order => order.id !== draggedData.id));
        setRightTableOrders(prev => [...prev, draggedData]);
      }
    } catch (error) {
      console.error('Error parsing dragged item:', error);
    }
  };

  // Pagination functions
  const totalPages = Math.ceil(totalItems / itemsPerPage);
  
  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setItemsPerPage(newItemsPerPage);
    setPage(1); // Reset to first page when changing items per page
  };

  const startItem = (page - 1) * itemsPerPage + 1;
  const endItem = Math.min(page * itemsPerPage, totalItems);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const SearchInput = ({ placeholder }: { placeholder: string }) => (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
      <input
        type="text"
        placeholder={placeholder}
        value={searchTerm}
        onChange={handleSearchChange}
        className="w-full rounded-full border border-[#D7DEEC] bg-white py-2 pl-10 pr-4 text-sm text-[#34415F] focus:border-[#C7D0E5] focus:outline-none focus:ring-2 focus:ring-[#E8EDF8]"
      />
    </div>
  );

  const TableHeader = ({ isLeftTable = true }: { isLeftTable?: boolean }) => {
    const isAllSelected = isLeftTable 
      ? leftTableOrders.length > 0 && selectedLeftItems.size === leftTableOrders.length
      : rightTableOrders.length > 0 && selectedRightItems.size === rightTableOrders.length;
    
    const handleSelectAll = isLeftTable ? handleLeftTableSelectAll : handleRightTableSelectAll;

    return (
      <thead className="bg-[#EDF1FF]">
        <tr>
          <th className="px-4 lg:px-6 py-3 text-left text-[12px] font-medium text-[#5E6784] border-r border-[#E7EBF3]">
            <CustomCheckbox 
              checked={isAllSelected} 
              onChange={handleSelectAll} 
            />
          </th>
          <th className="px-4 lg:px-6 py-3 text-left text-[12px] font-medium text-[#5E6784] border-r border-[#E7EBF3]">
            <div className="flex items-center gap-1">
              Identifier
              <ChevronsUpDown size={12} className="text-gray-400" />
            </div>
          </th>
          <th className="px-4 lg:px-6 py-3 text-left text-[12px] font-medium text-[#5E6784] border-r border-[#E7EBF3]">
            <div className="flex items-center gap-1">
              Order Date
              <ChevronsUpDown size={12} className="text-gray-400" />
            </div>
          </th>
          <th className="px-4 lg:px-6 py-3 text-left text-[12px] font-medium text-[#5E6784]">
            <div className="flex items-center gap-1">
              Zone
              <ChevronsUpDown size={12} className="text-gray-400" />
            </div>
          </th>
        </tr>
      </thead>
    );
  };

  const LeftTableBody = () => (
    <tbody className="bg-white divide-y divide-gray-200">
      {isLoading ? (
        <tr>
          <td colSpan={4} className="px-4 lg:px-6 py-8 text-center text-gray-500">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2">Loading...</span>
            </div>
          </td>
        </tr>
      ) : error ? (
        <tr>
          <td colSpan={4} className="px-4 lg:px-6 py-8 text-center text-red-500">
            {error}
          </td>
        </tr>
      ) : leftTableOrders.length === 0 ? (
        <tr>
          <td colSpan={4} className="px-4 lg:px-6 py-8 text-center text-gray-500">
            No orders found
          </td>
        </tr>
      ) : (
        leftTableOrders.map((order) => (
          <tr 
            key={order.id} 
            className="hover:bg-gray-50 cursor-move"
            draggable
            onDragStart={(e) => handleDragStart(e, order, true)}
          >
            <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-[#34415F] border-r border-[#EEF1F6]">
              <CustomCheckbox 
                checked={selectedLeftItems.has(order.id)}
                onChange={(checked) => handleLeftTableSelection(order.id, checked)}
              />
            </td>
            <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-[#34415F] border-r border-[#EEF1F6]">
              {order.identifier}
            </td>
            <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-[#34415F] border-r border-[#EEF1F6]">
              {order.orderDate}
            </td>
            <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-[#34415F]">
              {order.zone}
            </td>
          </tr>
        ))
      )}
    </tbody>
  );

  const RightTableBody = () => (
    <tbody className="bg-white divide-y divide-gray-200">
      {rightTableOrders.length === 0 ? (
        <tr>
          <td colSpan={4} className="px-4 lg:px-6 py-8 text-center text-gray-500">
            No items selected
          </td>
        </tr>
      ) : (
        rightTableOrders.map((order) => (
          <tr 
            key={order.id} 
            className="hover:bg-gray-50 cursor-move"
            draggable
            onDragStart={(e) => handleDragStart(e, order, false)}
          >
            <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-[#34415F] border-r border-[#EEF1F6]">
              <CustomCheckbox 
                checked={selectedRightItems.has(order.id)}
                onChange={(checked) => handleRightTableSelection(order.id, checked)}
              />
            </td>
            <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-[#34415F] border-r border-[#EEF1F6]">
              {order.identifier}
            </td>
            <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-[#34415F] border-r border-[#EEF1F6]">
              {order.orderDate}
            </td>
            <td className="px-4 lg:px-6 py-3 whitespace-nowrap text-sm text-[#34415F]">
              {order.zone}
            </td>
          </tr>
        ))
      )}
    </tbody>
  );

  const Footer = () => (
    <div className="flex flex-col gap-3 border-t border-[#E7EBF3] bg-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between lg:px-6">
      <div className="flex items-center gap-2 text-sm text-[#5E6784]">
        <span>Show</span>
        <select
          value={itemsPerPage}
          onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
          className="rounded-md border border-[#D7DEEC] bg-white px-2 py-1 text-sm text-[#34415F] focus:outline-none focus:ring-2 focus:ring-[#E8EDF8]"
        >
          <option value={5}>5</option>
          <option value={10}>10</option>
          <option value={20}>20</option>
        </select>
        <span>per page</span>
      </div>
      <div className="flex items-center gap-4 text-sm text-[#5E6784]">
        <span>{startItem}-{endItem} of {totalItems}</span>
        <div className="flex items-center gap-1">
          <button
            onClick={() => handlePageChange(Math.max(1, page - 1))}
            disabled={page === 1}
            className="rounded p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ChevronLeft className="h-4 w-4 text-[#5E6784]" />
          </button>
          {[1, 2, 3, 4, 5].map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => handlePageChange(pageNum)}
              className={`px-2.5 py-1 text-sm ${
                page === pageNum
                  ? 'rounded-md bg-[#F1F1F4] text-[#24304F]'
                  : 'text-[#5E6784] hover:bg-gray-100'
              }`}
            >
              {pageNum}
            </button>
          ))}
          <button
            onClick={() => handlePageChange(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="rounded p-1 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <ChevronRight className="h-4 w-4 text-[#5E6784]" />
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="mx-16 grid grid-cols-1 gap-6 xl:grid-cols-2">
      {/* Add Section */}
      <div 
        className="rounded-[20px] border border-[#E6EAF1] bg-[#F1F1F4] p-4 shadow-sm"
        onDragOver={handleLeftTableDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleLeftTableDrop}
      >
        <div className="mb-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
          <div className="w-full sm:w-[190px]">
              <SearchInput placeholder="Search for Orders" />
          </div>
          <button 
            onClick={handleAddItems}
            disabled={selectedLeftItems.size === 0}
            className="h-10 min-w-[112px] rounded-lg bg-[#FFC107] px-6 text-sm font-semibold text-[#1F2432] transition-colors hover:bg-[#F2B400] disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            Add
          </button>
        </div>
        <div className="overflow-hidden rounded-[18px] bg-white">
          <div className="overflow-x-auto">
          <table className="min-w-full">
            <TableHeader isLeftTable={true} />
            <LeftTableBody />
          </table>
          </div>
          <Footer />
        </div>
      </div>

      {/* Remove Section */}
      <div 
        className="rounded-[20px] border border-[#E6EAF1] bg-[#F1F1F4] p-4 shadow-sm"
        onDragOver={handleRightTableDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleRightTableDrop}
      >
        <div className={`mb-4 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center ${isDragOver ? 'rounded-xl bg-blue-50 px-3 py-2' : ''}`}>
          <div className="w-full sm:w-[190px]">
              <SearchInput placeholder="Search for Orders" />
          </div>
          <button 
            onClick={handleRemoveItems}
            disabled={selectedRightItems.size === 0}
            className="h-10 min-w-[112px] rounded-lg bg-[#FF2D75] px-6 text-sm font-semibold text-white transition-colors hover:bg-[#E82669] disabled:cursor-not-allowed disabled:bg-gray-300"
          >
            Remove
          </button>
        </div>
        <div className="overflow-hidden rounded-[18px] bg-white">
          <div className="overflow-x-auto">
          <table className="min-w-full">
            <TableHeader isLeftTable={false} />
            <RightTableBody />
          </table>
          </div>
          <Footer />
        </div>
      </div>
    </div>
  );
}
